import { readSse, ByteReader, TextDecoderLike } from './sse';
import {
  ChatRequest,
  ContextDocType,
  ContextFile,
  FeedbackRequest,
  FileStatus,
  ModelsResponse,
  SessionSummary,
  SseFrame,
  StoredTurn,
} from './types';

/**
 * The Query Tool's HTTP API (docs/query-tool/api-contract.md), under `/v1`.
 *
 * It uses `fetch`, not CENTERLINE's shared axios client: axios 0.21 cannot stream, the shared
 * rate limiter would hold a slot for a whole turn, and its error snackbars and error-report emails
 * are wrong for calls the chat deliberately lets fail quietly. Every call carries a project-scoped
 * token as a standard `Authorization: Bearer` header; the browser never names the user.
 */

/** The parts of `fetch` and `Response` this uses, so tests can pass a fake. */
export interface FetchResponse {
  ok: boolean;
  status: number;
  json(): Promise<unknown>;
  body?: { getReader(): ByteReader } | null;
}

export type FetchLike = (
  url: string,
  init: { method: string; headers: Record<string, string>; body?: string; signal?: AbortSignal },
) => Promise<FetchResponse>;

export type GetToken = (projectId: string, options?: { forceRefresh?: boolean }) => Promise<string>;

export interface QueryToolClientOptions {
  /** The Query Tool's origin, without `/v1` (see `queryToolBaseUrl`). */
  baseUrl: string;
  getToken: GetToken;
  fetchImpl?: FetchLike;
  decoder?: () => TextDecoderLike;
}

/** A non-2xx reply. `error` is the server's `error` text when the body had one. */
export class QueryToolHttpError extends Error {
  readonly status: number;

  readonly serverError?: string;

  constructor(status: number, serverError?: string) {
    super(`${status}: ${serverError ?? 'request failed'}`);
    Object.setPrototypeOf(this, QueryToolHttpError.prototype);
    this.name = 'QueryToolHttpError';
    this.status = status;
    this.serverError = serverError;
  }
}

/** Getting a token from CENTERLINE failed, so nothing was sent to the Query Tool. */
export class QueryToolTokenError extends Error {
  constructor(cause: unknown) {
    super(`Could not get a CENTERLINE AI token: ${String(cause)}`);
    Object.setPrototypeOf(this, QueryToolTokenError.prototype);
    this.name = 'QueryToolTokenError';
  }
}

export interface ChatStreamHandlers {
  onFrame(frame: SseFrame): void;
}

export interface QueryToolClient {
  getModels(projectId: string): Promise<ModelsResponse>;
  listSessions(projectId: string): Promise<SessionSummary[]>;
  getSession(projectId: string, sessionId: string): Promise<StoredTurn[]>;
  renameSession(projectId: string, sessionId: string, title: string): Promise<void>;
  archiveSession(projectId: string, sessionId: string): Promise<void>;
  sendFeedback(projectId: string, body: FeedbackRequest): Promise<void>;
  getDocTypes(projectId: string): Promise<ContextDocType[]>;
  getContextFiles(
    projectId: string,
    query: { docType?: string; q?: string; limit?: number },
  ): Promise<ContextFile[]>;
  /**
   * Where each file stands in the Query Tool's ingestion (at most 200 ids). Rejects with
   * `QueryToolHttpError` (404 from a Query Tool that predates the route, 503 when it cannot say).
   */
  getFileStatus(projectId: string, fileIds: string[]): Promise<FileStatus[]>;
  /**
   * Sends one turn and delivers its frames until the stream ends. Never retried: each attempt is
   * a full-price turn (contract §9.6). Rejects with `QueryToolHttpError` for a non-stream reply,
   * an `AbortError` when the signal aborts, or whatever the network threw.
   */
  streamChat(body: ChatRequest, handlers: ChatStreamHandlers, signal?: AbortSignal): Promise<void>;
}

/** A UUID as the Query Tool issues them; anything else must never be sent as an id (§9.4). */
export const SERVER_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const errorText = (data: unknown): string | undefined => {
  if (typeof data === 'object' && data !== null) {
    const { error } = data as { error?: unknown };
    if (typeof error === 'string') return error;
  }
  return undefined;
};

const readJson = async (res: FetchResponse): Promise<unknown> => {
  try {
    return await res.json();
  } catch {
    return {};
  }
};

export function createQueryToolClient(options: QueryToolClientOptions): QueryToolClient {
  const root = `${options.baseUrl.replace(/\/+$/, '')}/v1`;
  const doFetch: FetchLike =
    options.fetchImpl ?? ((url, init) => fetch(url, init) as unknown as Promise<FetchResponse>);

  const token = async (projectId: string, forceRefresh = false): Promise<string> => {
    try {
      return await options.getToken(projectId, { forceRefresh });
    } catch (err) {
      throw new QueryToolTokenError(err);
    }
  };

  const request = async <T>(
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
    path: string,
    projectId: string,
    body?: unknown,
  ): Promise<T> => {
    const send = async (forceRefresh: boolean) => {
      const headers: Record<string, string> = {
        Authorization: `Bearer ${await token(projectId, forceRefresh)}`,
      };
      if (body !== undefined) headers['Content-Type'] = 'application/json';
      return doFetch(`${root}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    };
    let res = await send(false);
    // A GET is retried once with a fresh token; anything that writes is never resent.
    if (res.status === 401 && method === 'GET') res = await send(true);
    const data = await readJson(res);
    if (!res.ok) throw new QueryToolHttpError(res.status, errorText(data));
    return data as T;
  };

  const assertId = (id: string) => {
    if (!SERVER_ID_RE.test(id)) throw new Error('Not a server-issued id.');
  };

  const pq = (projectId: string) => `project_id=${encodeURIComponent(projectId)}`;

  return {
    async getModels(projectId) {
      return request<ModelsResponse>('GET', '/models', projectId);
    },
    async listSessions(projectId) {
      const data = await request<{ sessions?: SessionSummary[] }>(
        'GET',
        `/sessions?${pq(projectId)}`,
        projectId,
      );
      return Array.isArray(data.sessions) ? data.sessions : [];
    },
    async getSession(projectId, sessionId) {
      assertId(sessionId);
      const data = await request<{ turns?: StoredTurn[] }>(
        'GET',
        `/sessions/${sessionId}`,
        projectId,
      );
      return Array.isArray(data.turns) ? data.turns : [];
    },
    async renameSession(projectId, sessionId, title) {
      assertId(sessionId);
      await request('PATCH', `/sessions/${sessionId}`, projectId, { title });
    },
    async archiveSession(projectId, sessionId) {
      assertId(sessionId);
      await request('DELETE', `/sessions/${sessionId}`, projectId);
    },
    async sendFeedback(projectId, body) {
      assertId(body.turn_id);
      await request('POST', '/feedback', projectId, body);
    },
    async getDocTypes(projectId) {
      const data = await request<{ doc_types?: ContextDocType[] }>(
        'GET',
        `/context/doctypes?${pq(projectId)}`,
        projectId,
      );
      return Array.isArray(data.doc_types) ? data.doc_types : [];
    },
    async getContextFiles(projectId, { docType, q, limit = 200 }) {
      const params = [pq(projectId), `limit=${limit}`];
      if (docType) params.push(`doc_type=${encodeURIComponent(docType)}`);
      if (q) params.push(`q=${encodeURIComponent(q)}`);
      const data = await request<{ files?: ContextFile[] }>(
        'GET',
        `/context/files?${params.join('&')}`,
        projectId,
      );
      return Array.isArray(data.files) ? data.files : [];
    },
    async getFileStatus(projectId, fileIds) {
      const data = await request<{ files?: FileStatus[] }>(
        'POST',
        '/context/files/status',
        projectId,
        {
          project_id: projectId,
          file_ids: fileIds,
        },
      );
      return Array.isArray(data.files) ? data.files : [];
    },
    async streamChat(body, handlers, signal) {
      if (body.session_id !== null) assertId(body.session_id);
      const res = await doFetch(`${root}/chat`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${await token(body.project_id)}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal,
      });
      if (!res.ok || !res.body) {
        throw new QueryToolHttpError(res.status, errorText(await readJson(res)));
      }
      await readSse(res.body.getReader(), handlers.onFrame, {
        signal,
        decoder: options.decoder?.(),
      });
    },
  };
}

/** The text a failed turn shows for a reply that was not a stream (chat-ui-spec §5.3). */
export function chatFailureText(err: unknown): string {
  if (err instanceof QueryToolHttpError) {
    const base = `${err.status}: ${err.serverError ?? 'request failed'}`;
    return err.status === 401
      ? `${base} — your CENTERLINE session could not be verified. Reload the page and try again.`
      : base;
  }
  if (err instanceof QueryToolTokenError) {
    return 'CENTERLINE AI could not verify your access to this project. Reload the page and try again.';
  }
  return String(err);
}
